import {
  FetchState,
  HsaLimitState,
  FetchStateKey
} from "@/shared/types";
import * as UserService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import moment from "moment";
import _ from "lodash";
import { fetchStateKeyMatch } from "@/shared/fetchStateHelper";
import { defineStore } from "pinia";
import { useHsaTotalStore } from "@/shared/store/hsaTotal";

const emptyState: HsaLimitState = {
  hsaLimitByYear: {},
  fetchState: FetchState.Uninitialized,
  fetchKey: null
};

export const useHsaLimitStore = defineStore("hsaLimit", {
  state: (): HsaLimitState => (_.cloneDeep(emptyState)),
  getters: {
    getHsaLimitByYear (): (year?: number) => number {
      return (year?: number) => {
        if (!year) {
          year = moment().year();
        }
        const limit = this.hsaLimitByYear[year];
        if (limit) {
          return limit.limit;
        }

        if (Object.keys(this.hsaLimitByYear).length > 0) {
          const maxYearLimit = this.hsaLimitByYear[Math.max.apply(null, Object.keys(this.hsaLimitByYear))];
          return year > +maxYearLimit.year ? maxYearLimit.limit : 0;
        }

        return 0;
      };
    },
    hsaContributionLimitsState: state => state.fetchState,
    otherHsaAmount () {
      return (year?: number) => {
        const hsaTotalStore = useHsaTotalStore();
        return hsaTotalStore.otherHsaAmount(year);
      };
    }
  },
  actions: {
    invalidateFetchState (key: FetchStateKey) {
      if (fetchStateKeyMatch(key, this.fetchKey)) {
        return;
      }
      const stateClone = _.cloneDeep(emptyState);
      for (const key in stateClone) {
        this[key] = stateClone[key];
      }
    },
    setHsaLimits (limits) {
      const dictionary = {};

      limits.forEach(limit => {
        dictionary[limit.year] = limit;
      });

      this.hsaLimitByYear = dictionary;
      this.fetchState = FetchState.Loaded;
    },
    setOtherHsaAmount (otherHsaAmount: number) {
      const year = moment().year();
      const limit = this.hsaLimitByYear[year];
      if (limit) {
        limit.otherHSAAmount = otherHsaAmount;
      }
    },
    setHsaLimitsLoading (fetchKey: FetchStateKey) {
      this.fetchState = FetchState.Loading;
      this.fetchKey = fetchKey;
    },
    getHsaAccountLimits (
      args?: { username?: string, partnerId?: number }) {

      args = args || {};

      const fetchStateKey: FetchStateKey = {
        username: args.username,
        partnerId: args.partnerId
      };

      this.invalidateFetchState(fetchStateKey);

      if (this.fetchState !== FetchState.Uninitialized) {
        return Promise.resolve();
      }

      this.setHsaLimitsLoading(fetchStateKey);

      return UserService.contributionLimits(args.username, args.partnerId)
        .then(summary => {
          if (this.fetchKey === fetchStateKey) {
            this.setHsaLimits(summary.data);
          }
        })
        .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
    }
  }
});